'use client';

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { ICamera } from '@/data/interfaces/camera';
import CameraControlsWindow from '@/components/camera/CameraControlsWindow';

interface WindowInstance {
  id: string;
  cameraId: string;
  cameraData: ICamera;
  alertId?: string;
  locationId?: number;
}

interface CameraControlsContextType {
  openWindows: Map<string, WindowInstance>;
  isWindowOpen: (cameraId: string) => boolean;
  openWindow: (cameraId: string, cameraData: ICamera, alertId?: string, locationId?: number) => void;
  closeWindow: (cameraId: string) => void;
  closeAllWindows: () => void;
  getCurrentCamera: (cameraId: string) => ICamera | null;
  getWindowData: (cameraId: string) => { alertId?: string; locationId?: number } | null;
}

const CameraControlsContext = createContext<CameraControlsContextType | undefined>(undefined);

interface CameraControlsProviderProps {
  children: React.ReactNode;
}

export const CameraControlsProvider: React.FC<CameraControlsProviderProps> = ({ children }) => {
  const [openWindows, setOpenWindows] = useState<Map<string, WindowInstance>>(new Map());
  const windowDataRef = useRef<Map<string, { alertId?: string; locationId?: number }>>(new Map());

  const isWindowOpen = useCallback((cameraId: string): boolean => {
    console.log('🔍 isWindowOpen called for camera:', cameraId);
    const isOpen = openWindows.has(cameraId);
    console.log('🔍 Window open status:', isOpen);
    return isOpen;
  }, [openWindows]);

  const openWindow = useCallback((cameraId: string, cameraData: ICamera, alertId?: string, locationId?: number) => {
    const effectiveLocationId = locationId || cameraData.LocationId;
    
    console.log('🚀 openWindow called with:', {
      cameraId,
      cameraName: cameraData?.name,
      alertId,
      locationId: effectiveLocationId,
      cameraPlayerId: cameraData?.cameraPlayerId
    });

    if (isWindowOpen(cameraId)) {
      console.log('🔄 Window already open, closing first');
      closeWindow(cameraId);
    }

    const windowInstance: WindowInstance = {
      id: cameraId,
      cameraId,
      cameraData,
      alertId,
      locationId: effectiveLocationId
    };

    windowDataRef.current.set(cameraId, { alertId, locationId: effectiveLocationId });

    setOpenWindows(prev => new Map(prev.set(cameraId, windowInstance)));
    
    console.log('✅ Window opened successfully');
  }, [isWindowOpen]);

  const closeWindow = useCallback((cameraId: string) => {
    console.log('❌ closeWindow called for camera:', cameraId);
    
    setOpenWindows(prev => {
      const newMap = new Map(prev);
      newMap.delete(cameraId);
      return newMap;
    });
    
    windowDataRef.current.delete(cameraId);
    console.log('✅ Window closed successfully');
  }, []);

  const closeAllWindows = useCallback(() => {
    console.log('🗑️ closeAllWindows called');
    setOpenWindows(new Map());
    windowDataRef.current.clear();
  }, []);

  const getCurrentCamera = useCallback((cameraId: string): ICamera | null => {
    const instance = openWindows.get(cameraId);
    return instance?.cameraData || null;
  }, [openWindows]);

  const getWindowData = useCallback((cameraId: string) => {
    return windowDataRef.current.get(cameraId) || null;
  }, []);

  const contextValue: CameraControlsContextType = {
    openWindows,
    isWindowOpen,
    openWindow,
    closeWindow,
    closeAllWindows,
    getCurrentCamera,
    getWindowData
  };

  return (
    <CameraControlsContext.Provider value={contextValue}>
      {children}
      
      {/* Render all open windows */}
      {Array.from(openWindows.values()).map((instance) => (
        <CameraControlsWindow
          key={instance.id}
          cameraId={instance.cameraId}
          cameraData={instance.cameraData}
          isOpen={true}
          onClose={() => closeWindow(instance.cameraId)}
          alertId={instance.alertId}
          locationId={instance.locationId}
          zIndex={1000 + openWindows.size}
        />
      ))}
    </CameraControlsContext.Provider>
  );
};

export const useCameraControls = (): CameraControlsContextType => {
  const context = useContext(CameraControlsContext);
  if (context === undefined) {
    throw new Error('useCameraControls must be used within a CameraControlsProvider');
  }
  return context;
};